import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Clock, Star, X, CheckCircle } from 'lucide-react';

const PROMO_IMAGE = "https://images.unsplash.com/photo-1554224154-22dec7ec8818?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHx0YXglMjBwcmVwYXJhdGlvbnxlbnwwfHx8fDE3ODY4MjAxNDJ8MA&ixlib=rb-4.1.0&q=85&w=1200";
const PROMO_IMAGE_2 = "https://images.pexels.com/photos/6801680/pexels-photo-6801680.jpeg?auto=compress&cs=tinysrgb&w=800";

export const PromoBanner = () => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgb(0, 55, 32) 0%, rgb(5, 75, 45) 40%, rgb(10, 95, 55) 100%)' }}>
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

      {/* Close button */}
      <button onClick={() => setDismissed(true)} className="absolute top-4 right-4 z-20 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Dismiss banner">
        <X className="w-4 h-4 text-white/70" />
      </button>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 py-0">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-0 items-stretch min-h-[480px]">

          {/* Left — Content */}
          <div className="flex flex-col justify-center py-14 lg:py-16 lg:pr-10">
            {/* Limited time badge */}
            <div className="inline-flex items-center gap-2 bg-[#8FEC78]/15 border border-[#8FEC78]/30 rounded-full px-4 py-1.5 w-fit mb-6">
              <span className="w-2 h-2 rounded-full bg-[#8FEC78] animate-pulse" />
              <span className="text-[#8FEC78] text-xs font-bold tracking-wider uppercase">Now Accepting New Clients</span>
            </div>

            <h2 className="text-white font-bold tracking-tight leading-[1.05] mb-5" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.8rem)' }}>
              Expert Tax Preparation<br />
              <span className="text-[#8FEC78]">Starting at Just $295</span>
            </h2>

            <p className="text-white/70 text-base leading-relaxed mb-6 max-w-[520px]">
              Why pay more at H&R Block? Get personalized, expert-level tax preparation with 20+ years of experience. 2-day turnaround. No upsells. No hidden fees.
            </p>

            {/* Comparison points */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {[
                { icon: Shield, text: 'Personal attention — not a number' },
                { icon: Clock, text: '2-day turnaround guaranteed' },
                { icon: Star, text: '20+ years of tax expertise' },
                { icon: CheckCircle, text: 'Free consultation — no obligation' },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-[#8FEC78]/15 flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-[#8FEC78]" />
                  </div>
                  <span className="text-white/85 text-sm">{text}</span>
                </div>
              ))}
            </div>

            {/* Pricing comparison */}
            <div className="bg-white/[0.06] backdrop-blur-sm border border-white/10 rounded-xl p-5 mb-8 max-w-[480px]">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white/50 text-xs font-semibold uppercase tracking-wider">Compare & Save</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-white/40 text-xs mb-1 line-through">Big Box Tax Chains</div>
                  <div className="text-white/50 text-xl font-bold">$450+</div>
                  <div className="text-white/30 text-[10px] mt-0.5">Average cost + upsells</div>
                </div>
                <div className="text-center bg-[#8FEC78]/10 rounded-lg py-2">
                  <div className="text-[#8FEC78] text-xs mb-1 font-semibold">Beginning to End Tax</div>
                  <div className="text-white text-xl font-bold">$295</div>
                  <div className="text-[#8FEC78]/70 text-[10px] mt-0.5">Starting at • all-inclusive</div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start gap-3">
              <Link to="/book" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-[rgb(0,55,32)] font-bold text-base shadow-lg shadow-[#8FEC78]/20 hover:shadow-xl hover:shadow-[#8FEC78]/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 no-underline">
                Book Free Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/start-here" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/20 text-white font-medium text-base no-underline hover:bg-white/10 transition-colors duration-200">
                Start Here
              </Link>
            </div>
          </div>

          {/* Right — Image */}
          <div className="hidden lg:block relative">
            <div className="absolute inset-0 overflow-hidden rounded-tl-[40px]">
              <img
                src={PROMO_IMAGE}
                alt="Professional tax preparation workspace"
                className="w-full h-full object-cover"
                loading="eager"
              />
              {/* Gradient overlay for blend */}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgb(0, 55, 32) 0%, transparent 30%)' }} />
              <div className="absolute inset-0 bg-[rgb(0,55,32)]/20" />
            </div>

            {/* Floating stat card */}
            <div className="absolute bottom-8 left-8 bg-white/95 backdrop-blur-lg rounded-xl p-4 shadow-xl max-w-[200px]">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] flex items-center justify-center">
                  <Star className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-[rgb(0,55,32)] text-lg font-bold leading-none">20+</div>
                  <div className="text-[rgb(131,146,140)] text-[10px]">Years Experience</div>
                </div>
              </div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-[#8FEC78] text-[#8FEC78]" />
                ))}
              </div>
              <p className="text-[rgb(131,146,140)] text-[10px] mt-1">Trusted by clients nationwide</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom accent line */}
      <div className="h-1 bg-gradient-to-r from-[#8FEC78] via-[#81DD67] to-[#8FEC78]" />
    </section>
  );
};
